import { ColorEnum } from '@/constants/color'
import { useRouterCanGoBackParamSubscriber } from '@/features/navigation/hooks/can-go-back'
import EmptyState from '@/features/openTabs/components/EmptyState'
import SplitColumn from '@/features/openTabs/components/SplitColumn'
import SplitHeader from '@/features/openTabs/components/SplitHeader'
import SplitSelectionFooter from '@/features/openTabs/components/SplitSelectionFooter'
import SplitTotal from '@/features/openTabs/components/SplitTotal'
import { useOpenTabHeader } from '@/features/openTabs/hooks/useOpenTabHeader'
import useSplitAccountPage from '@/features/openTabs/pages/SplitAccountPage/hooks/useSplitAccountPage'
import { useScreenLayout } from '@/hooks/screen'
import { Redirect } from 'expo-router'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

const SplitAccountPage: FC = () => {
    useRouterCanGoBackParamSubscriber()

    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet)
    const {
        selectedAccount,
        remainingItems,
        selectedItems,
        paymentTotalPrice,
        remainingTotalPrice,
        canContinue,
        selectionDisabled,
        handleContinue,
        handleCancel,
        handleMoveToSplit,
        handleMoveFromSplit,
    } = useSplitAccountPage()
    const [reviewing, setReviewing] = useState(false)
    useOpenTabHeader({
        title: t('openTabs.actions.split'),
        onBack: layout === 'compact' && reviewing ? () => setReviewing(false) : handleCancel,
    })

    if (!selectedAccount || selectedAccount.status === 'closed') {
        return <Redirect href="/open-tabs" />
    }

    const footer = (compact = false) => (
        <SplitSelectionFooter
            totalPrice={paymentTotalPrice}
            canContinue={canContinue}
            reviewing={!compact || reviewing}
            onCancel={handleCancel}
            onContinue={compact && !reviewing ? () => setReviewing(true) : handleContinue}
        />
    )
    const emptyPayment = (
        <EmptyState
            title={t('openTabs.empty.splitTitle')}
            description={t('openTabs.empty.splitDescription')}
        />
    )
    const header = (
        <SplitHeader accountName={selectedAccount.name} accountNumber={selectedAccount.number} />
    )

    return (
        <View style={styles.container}>
            {layout === 'compact' ? (
                <View style={styles.compactContainer}>
                    <ScrollView
                        contentContainerStyle={styles.compactContent}
                        style={styles.compactScroll}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                    >
                        {header}
                        {reviewing && !selectedItems.length ? (
                            emptyPayment
                        ) : (
                            <SplitColumn
                                title={t(
                                    reviewing ? 'openTabs.split.toPay' : 'openTabs.split.orderItems'
                                )}
                                items={reviewing ? selectedItems : remainingItems}
                                direction={reviewing ? 'back' : 'forward'}
                                disabled={selectionDisabled}
                                scrollable={false}
                                onMove={reviewing ? handleMoveFromSplit : handleMoveToSplit}
                            />
                        )}
                    </ScrollView>
                    {footer(true)}
                </View>
            ) : (
                <>
                    <View style={styles.column}>
                        {header}
                        <SplitColumn
                            title={t('openTabs.split.orderItems')}
                            items={remainingItems}
                            disabled={selectionDisabled}
                            direction="forward"
                            onMove={handleMoveToSplit}
                        />
                        <SplitTotal totalPrice={remainingTotalPrice} />
                    </View>
                    <View style={styles.rightColumn}>
                        {selectedItems.length ? (
                            <>
                                <View style={styles.rightHeaderSpacer} />
                                <SplitColumn
                                    title={t('openTabs.split.toPay')}
                                    items={selectedItems}
                                    disabled={selectionDisabled}
                                    direction="back"
                                    onMove={handleMoveFromSplit}
                                />
                            </>
                        ) : (
                            emptyPayment
                        )}
                        {footer()}
                    </View>
                </>
            )}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: { flex: 1, flexDirection: 'row', backgroundColor: ColorEnum.GRAY_100 },
    column: {
        flex: 1,
        minWidth: 0,
        gap: 16,
        paddingTop: 16,
        borderRightWidth: 1,
        borderRightColor: ColorEnum.GRAY_200,
    },
    rightColumn: { flex: 1, minWidth: 0, paddingTop: 16 },
    rightHeaderSpacer: { height: 26, marginBottom: 16 },
    compactContainer: { flex: 1, minWidth: 0, minHeight: 0, backgroundColor: ColorEnum.WHITE },
    compactScroll: { flex: 1, minHeight: 0 },
    compactContent: { flexGrow: 1, paddingTop: 16, gap: 16 },
})

export default SplitAccountPage
